import { Link, useLocation } from 'react-router-dom';
import { Box, Flex, Icon, Text, useColorModeValue, VStack, Badge } from '@chakra-ui/react';
import { FiHome, FiClock, FiShoppingBag, FiGrid, FiUser } from 'react-icons/fi';
import { useUserDefaultRole } from '@nhost/react';
import { Roles } from 'src/utils';
import { ElementType } from 'react';

interface NavItem {
  label: string;
  href: string;
  icon: ElementType;
  badge?: number;
}

const BottomNavigation = () => {
  const location = useLocation();
  const userDefaultRole = useUserDefaultRole();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const activeColor = useColorModeValue('brand.500', 'brand.300');
  const inactiveColor = useColorModeValue('gray.500', 'gray.400');
  const activeBg = useColorModeValue('brand.50', 'whiteAlpha.100');

  const adminNavItems: NavItem[] = [
    { label: 'Dashboard', href: 'admin', icon: FiHome as ElementType },
    { label: 'Categories', href: 'categories', icon: FiGrid as ElementType },
    { label: 'Orders', href: 'order', icon: FiShoppingBag as ElementType },
    { label: 'History', href: 'history', icon: FiClock as ElementType },
    { label: 'Profile', href: 'profile', icon: FiUser as ElementType },
  ];

  const restaurantNavItems: NavItem[] = [
    { label: 'Overview', href: 'overview', icon: FiHome as ElementType },
    { label: 'Orders', href: 'order', icon: FiShoppingBag as ElementType },
    { label: 'Menu', href: 'menu', icon: FiGrid as ElementType },
    { label: 'History', href: 'history', icon: FiClock as ElementType },
    { label: 'Profile', href: 'profile', icon: FiUser as ElementType },
  ];

  const navItems = userDefaultRole === Roles.ADMIN ? adminNavItems : restaurantNavItems;

  const isActive = (href: string) => {
    const path = location.pathname.split('/')[1];
    return path === href;
  };

  return (
    <Box
      display={{ base: 'block', md: 'none' }}
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      zIndex={1000}
      bg={bgColor}
      borderTop="1px"
      borderColor={borderColor}
      boxShadow="0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 -2px 4px -1px rgba(0, 0, 0, 0.06)"
      backdropFilter="blur(10px)"
      sx={{
        '@supports (backdrop-filter: blur(10px))': {
          bg: useColorModeValue('rgba(255, 255, 255, 0.95)', 'rgba(26, 32, 44, 0.95)'),
        },
      }}
    >
      <Flex justify="space-around" align="center" py={2}>
        {navItems.map((item) => {
          const active = isActive(item.href);
          return (
            <Link key={item.href} to={item.href} style={{ textDecoration: 'none' }}>
              <VStack
                spacing={1}
                p={2}
                borderRadius="lg"
                bg={active ? activeBg : 'transparent'}
                color={active ? activeColor : inactiveColor}
                transition="all 0.2s"
                _hover={{
                  bg: activeBg,
                  transform: 'translateY(-1px)',
                }}
                position="relative"
                minW="60px"
              >
                <Icon as={item.icon as ElementType} boxSize={5} transition="all 0.2s" />
                <Text fontSize="xs" fontWeight={active ? '600' : '500'} textAlign="center" lineHeight="1">
                  {item.label}
                </Text>
                {item.badge && (
                  <Badge
                    position="absolute"
                    top={1}
                    right={1}
                    colorScheme="red"
                    size="sm"
                    borderRadius="full"
                    fontSize="10px"
                    minW="16px"
                    h="16px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {item.badge}
                  </Badge>
                )}
              </VStack>
            </Link>
          );
        })}
      </Flex>
    </Box>
  );
};

export default BottomNavigation;
